import React from 'react';
import { Building2, Activity, Heart, Stethoscope, ArrowRight } from 'lucide-react';

const HospitalShowcase = () => {
  const showcaseItems = [
    {
      image: 'https://customer-assets.emergentagent.com/job_hospital-loan-hub/artifacts/gxkjc5tl_Screenshot_20260602_101255_ChatGPT.jpg',
      title: 'Modern Hospital Infrastructure',
      description: 'State-of-the-art facilities for world-class healthcare',
      icon: Building2,
      stat: '100+',
      statLabel: 'Hospitals Funded'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_hospital-loan-hub/artifacts/hw4nb0fj_Screenshot_20260602_101306_ChatGPT.jpg',
      title: 'Complete Hospital Facilities',
      description: 'OPD, IPD, OT, ICU & reception - we finance it all',
      icon: Activity,
      stat: '₹100Cr+',
      statLabel: 'Equipment Financed'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_hospital-loan-hub/artifacts/8t3e4jfa_Screenshot_20260602_103350_ChatGPT.jpg',
      title: 'OT & ICU Setup',
      description: 'Complete operating theatre and ICU solutions',
      icon: Heart,
      stat: '200+',
      statLabel: 'OT/ICU Projects'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-72 h-72 bg-amber-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 text-amber-200 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Hospital Infrastructure
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Building <span className="bg-gradient-to-r from-amber-400 to-amber-200 bg-clip-text text-transparent">World-Class</span> Healthcare
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            From single clinics to multi-specialty hospitals, we finance every aspect of healthcare infrastructure
          </p>
        </div>

        {/* Showcase Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {showcaseItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image */}
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900 via-blue-900/60 to-transparent"></div>

                  {/* Icon Badge */}
                  <div className="absolute top-6 right-6 w-14 h-14 bg-amber-500 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Stat Badge */}
                  <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-xl">
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-amber-500 bg-clip-text text-transparent">
                      {item.stat}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">{item.statLabel}</div>
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-amber-300 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-blue-100 mb-4 leading-relaxed">{item.description}</p>
                    <div className="flex items-center gap-2 text-amber-400 font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 max-w-3xl">
            <div className="flex-1 text-left">
              <h3 className="text-xl font-bold text-white mb-1">Planning a Hospital Project?</h3>
              <p className="text-blue-200 text-sm">Get up to 100% financing for your healthcare venture</p>
            </div>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HospitalShowcase;
